import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BarChart3, LineChart, Activity } from 'lucide-react';
import AnimatedSection from '../AnimatedSection';

const FeaturesGrid = () => {
  const { scrollYProgress } = useScroll();
  
  const featureCards = [
    {
      id: 'indicators',
      icon: <BarChart3 size={32} />,
      title: 'Indicators',
      description: 'Professional technical and volume-based indicators for comprehensive market analysis',
      color: '#7C3AED'
    },
    {
      id: 'algorithm',
      icon: <LineChart size={32} />,
      title: 'Algorithm Trading',
      description: 'Build, test, and deploy sophisticated trading algorithms with ease',
      color: '#00D4FF'
    },
    {
      id: 'ai-analyst',
      icon: <Activity size={32} />,
      title: 'AI Analyst',
      description: 'AI-powered insights and predictive analytics to enhance your decisions',
      color: '#ec4899'
    }
  ];

  return (
    <section className="py-24 bg-[#0F172A] relative overflow-hidden">
      {/* Animated Background Elements */}
      <motion.div
        className="absolute top-20 right-20 w-64 h-64 bg-[#7C3AED]/10 rounded-full blur-[100px]"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 left-20 w-64 h-64 bg-[#00D4FF]/10 rounded-full blur-[100px]"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.3, 0.6, 0.3]
        }}
        transition={{ duration: 10, repeat: Infinity, delay: 2 }}
      />

      <div className="max-w-[1280px] mx-auto px-8 relative z-10">
        <AnimatedSection>
          <motion.h2 
            className="text-4xl font-bold text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Powerful Features for Professional Traders
          </motion.h2>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" style={{ perspective: '1000px' }}>
          {featureCards.map((card, index) => (
            <AnimatedSection key={card.id} delay={index * 0.1}>
              <Link to={`/features/${card.id}`}>
                <motion.div
                  className="relative bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-8 h-full hover:border-[#7C3AED]/50 transition-all cursor-pointer group"
                  initial={{ opacity: 0, y: 50, rotateX: 10 }}
                  whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
                  viewport={{ once: true }}
                  transition={{ 
                    duration: 0.8, 
                    delay: index * 0.2,
                    type: 'spring',
                    stiffness: 100
                  }}
                  whileHover={{ 
                    scale: 1.05,
                    rotateY: 5,
                    rotateX: -5,
                    translateZ: 50,
                    boxShadow: `0 30px 80px ${card.color}40`,
                    transition: { duration: 0.4 }
                  }}
                  style={{
                    transformStyle: 'preserve-3d'
                  }}
                >
                  {/* Glowing Background on Hover */}
                  <motion.div
                    className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                      background: `radial-gradient(circle at 50% 50%, ${card.color}20 0%, transparent 70%)`
                    }}
                  />

                  {/* Animated Icon */}
                  <div 
                    className="w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: `${card.color}20`, color: card.color }}
                  >
                    {card.icon}
                  </div>

                  <h3 className="text-2xl font-bold mb-4">{card.title}</h3>
                  <p className="text-[#9CA3AF] leading-relaxed">{card.description}</p>
                  
                  {/* Feature Link Indicator */}
                  <div className="mt-8 flex items-center gap-2 text-sm font-semibold transition-all group-hover:gap-4" style={{ color: card.color }}>
                    LEARN MORE <span className="text-lg">→</span>
                  </div>
                </motion.div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesGrid;
